<script>
    import mapboxgl from 'mapbox-gl';
    import { onMount } from 'svelte';
    import Legend from './components/Legend.svelte';
    import Locator from './components/Locator.svelte';
    
    // Receive data as props from parent
    let { width, forecast, manifestData, loadedData, copy, params, onMapChange } = $props();
    
    const initLat = 37.5;
    const initLng = -96;
    let mapContainer = $state();
    let map = $state();
    let lat = $derived(params.lat || initLat);
    let lng = $derived(params.lng || initLng);
    let zoom = $derived(params.zoom || 3.5);

    // variable to track if map is focused on most severe
    let focused = $derived(width < 500)
    // Track if user has manually interacted with the map
    let userHasInteracted = $state(false);

    // Set your Mapbox access token here
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJja2V1M3Vyd3kwMDZrMnF0Njdhb3ZhZDk5In0.OURIYzTcS6ZzoWjXJzfANg';
    
    // Calculate responsive height based on width using $derived
    let height = $state(600);
    let bounds = $state(null);
    
    // Adjust zoom level for mobile vs desktop using $derived
    // let zoom = $derived(width < 600 ? 2.8 : 3.5);
    
    let stylesLoaded = $state(false);

    // Initialize map on mount
    onMount(() => {
        if (!mapContainer) return;
        
        // If lat/lng/zoom are in URL, user has previously interacted
        if (params.lat || params.lng || params.zoom) {
            userHasInteracted = true;
        }
        
        // Initialize the map
        map = new mapboxgl.Map({
            container: mapContainer,
            style: 'mapbox://styles/cnndigital/cmhnmfg9j005h01qtfmi7chxr',
            zoom: zoom,
            projection: 'albers',
            center: [lng, lat],
            // Restrict bounds to continental US + minimal buffer
            maxBounds: [
                [-126, 23.5],  // Southwest coordinates [lng, lat]
                [-65, 50]      // Northeast coordinates [lng, lat]
            ],
            minZoom: 3,      // Prevent zooming out too far
            maxZoom: 12,     // Prevent zooming in too far
            cooperativeGestures: true,  // Require ctrl+scroll or two fingers to zoom
            // parallels: [29.5, 45.5],
        });
        
        // Add navigation controls
        map.addControl(new mapboxgl.NavigationControl(
            {showCompass: false}
        ), 'top-right');
        
        // Track user interactions to know when to update URL
        map.on('dragstart', () => { userHasInteracted = true; });
        map.on('zoomstart', (e) => {
            // Only mark as interacted if it's a user-initiated zoom (not programmatic)
            if (e.originalEvent) {
                userHasInteracted = true;
            }
        });
        map.on('touchstart', () => { userHasInteracted = true; });
        map.on('dblclick', () => { userHasInteracted = true; });
        
        // Fit to contiguous US bounds on load
        map.on('load', () => {
            stylesLoaded = true;
            // Bounds for contiguous United States
            // const bounds = [
            //     [-125.0, 24.5], // Southwest coordinates [lng, lat]
            //     [-66.9, 49.4]   // Northeast coordinates [lng, lat]
            // ];

            bounds = map.getBounds();
            
            // map.fitBounds(bounds, {
            //     padding: { top: 20, bottom: 20, left: 20, right: 20 },
            //     maxZoom: zoom
            // });

            // console.log(map.getStyle().layers)
        });

        map.on('moveend', () => {
            const currentZoom = map.getZoom();
            const mapCenter = map.getCenter();
            const currentLat = mapCenter.lat;
            const currentLng = mapCenter.lng;

            // console.log(`?lat=${currentLat}&lng=${currentLng}&zoom=${currentZoom}`);
            
            // Always update bounds for the locator
            bounds = map.getBounds();
            
            // Only update URL and send to parent if user has manually interacted
            if (userHasInteracted) {
                const currentParams = new URLSearchParams(window.location.search);
                
                // Update URL params
                currentParams.set('lat', currentLat);
                currentParams.set('lng', currentLng);
                currentParams.set('zoom', currentZoom);
                window.history.pushState({}, '', `${window.location.pathname}?${currentParams.toString()}`);
                
                // Send to parent window if callback provided
                if (onMapChange) {
                    onMapChange({
                        zoom: currentZoom,
                        lat: currentLat,
                        lng: currentLng
                    });

                }
            }
        });
        
        // Cleanup on destroy
        return () => {
            if (map) {
                map.remove();
            }
        };
    });
    
    
    // Update zoom when width changes
    // $effect(() => {
    //     if (map) {
    //         map.setZoom(zoom);
    //     }
    // });

    $effect(() => {
        // Only apply app_center_coords if user hasn't specified custom coordinates via query params
        if (focused && !params.lat && !params.lng && !params.zoom) {
            if (manifestData && manifestData['forecasts'][forecast].app_center_coords) {
                map.setCenter([manifestData['forecasts'][forecast].app_center_coords[1], initLat])
            }
        }
    });


    

    $effect(() => {
        if (loadedData && stylesLoaded) {
            map.addSource('outlook', {
                type: 'geojson',
                data: loadedData
            });

            // map.addSource('us-states', {
            //     type: 'geojson',
            //     data: 'https://ix.cnn.io/data/gis/usa_states_conus.geojson',
            // });

            const tgtLayer = 'building';
            
            map.addLayer({
                id: 'outlook',
                source: 'outlook',
                type: 'fill',
                paint: {
                'fill-color': ['get', 'fill'],
                },
            // Layer in the style stack under which this layer should be drawn
            }, tgtLayer);

            // map.addLayer({
            //     id: borderLayerId,
            //     type: 'line',
            //     source: 'us-states',
            //     paint: {
            //     'line-color': '#ffffff',
            //     'line-width': 1,
            //     },
            // }, 'outlook');
        }
    });

</script>
{#if forecast && loadedData && manifestData }
    <Legend {copy} scale={manifestData.color_scale}/>
{/if}
<div class="map-wrapper">
    {#if focused }
        <div class="locator-container">
            <Locator loadedData={loadedData} bounds={bounds} />
        </div>
    {/if}
    <div bind:this={mapContainer} class="map-container" style="height: {height}px"></div>
</div>

<style lang='scss'>
    .map-wrapper {
        width: 100%;
        position: relative;
        height: 600px;
    }
    .locator-container {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 1;
    }
    
    .map-container {
        width: 100%;
        overflow: hidden;
    }
    
    :global(.mapboxgl-ctrl-attrib) {
        font-size: 10px;
    }
    
    :global(.mapboxgl-ctrl-logo) {
        margin: 0 0 2px 2px;
    }
</style>
